import { Severity, WithState } from '@main/shared/browser';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ComponentType, ReactNode, SVGProps, useEffect, useMemo } from 'react';

import { ITabsService, TabsService } from './tabs-service';
import { ActiveTabNamespaceSuffix, DefaultTabNameSpace, TabId } from './types';

export interface TabBade {
  count: number | undefined;
  severity: Severity | undefined;
}
export interface TabItem<TId extends TabId = TabId> {
  id: TId;
  label: string;
  alternateLabelForDrawerHeading?: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  panel: WithState<ReactNode, TabItem<TId>>;
  badge?: TabBade;
  isDefault?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  hideTabTitle?: boolean;
}

export interface UseTabsProps<TId extends TabId = TabId> {
  tabs?: TabItem<TId>[];
  namespace?: string;
  /**
   * Keep the window scroll position when switching tabs. Set it for tabs that
   * live inside an overlay (the entity drawer), where the page behind the
   * overlay does not change and must not jump to the top. Page-level tabs swap
   * the whole page content, so they keep the router default of scrolling up.
   */
  preserveScroll?: boolean;
}

export function useTabs<TId extends TabId = TabId>({
  tabs,
  namespace,
  preserveScroll = false,
}: UseTabsProps<TId>): ITabsService<TId> {
  const tabNameSpace = namespace ? `${namespace}${ActiveTabNamespaceSuffix}` : DefaultTabNameSpace;
  const navigate = useNavigate();

  const searchParam = useSearch({
    strict: false,
    select: (search: Record<string, unknown>) => search[tabNameSpace] as TId | undefined,
  });

  const tabsService = useMemo(
    () =>
      new TabsService({
        tabs: tabs?.map((tab) => tab.id) ?? [],
        disabledTabs: tabs?.filter((tab) => tab.isDisabled).map((tab) => tab.id) ?? [],
        defaultTab: tabs?.find((tab) => tab.isDefault)?.id,
        navigate: (tab, replace) =>
          navigate({
            to: '.',
            search: (old: Record<string, unknown>) => ({ ...old, [tabNameSpace]: tab }),
            params: (old: Record<string, unknown>) => old,
            replace,
            resetScroll: !preserveScroll,
          }),
      }),
    [tabs, navigate, tabNameSpace, preserveScroll],
  );

  useEffect(
    () => tabsService.setActiveTab(searchParam as TId | undefined),
    [searchParam, tabsService],
  );

  return tabsService;
}
